/**
 * Magic-link sign-in entry. Calls `auth.signIn(email)` (real cross-
 * origin POST per ADR-0018 §3) and transitions to a "check your
 * email" confirmation pane on success (204 anti-enumeration per
 * ADR-0011 §5.1). 429 surfaces a retry-with-minutes copy; any other
 * failure surfaces a soft-retry copy without flipping the success
 * state. Same status-code-mapping shape as the marketing site's
 * `submitSignIn` helper (`apps/marketing/src/lib/sign-in.ts`).
 *
 * The "link expires in 10 minutes" copy reflects ADR-0011 §1.1 (and
 * ADR-0017 §8 confirms the token TTL stays at 10 minutes through
 * the cookie-posture amendment).
 */

import { ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';

import { AuthRateLimitError } from '@sill/types/api';

import { useApi } from '../../api/api-context.js';
import type { View } from '../../app/view-router.js';
import { sanitizePrefillDomainParam } from '../../lib/prefill-domain.js';
import { useC } from '../../lib/theme.js';
import { NumLabel } from '../../components/atoms/index.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface LoginProps {
  setView: (v: View) => void;
}

export function Login({ setView }: LoginProps): JSX.Element {
  const C = useC();
  const api = useApi();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorRetryable, setErrorRetryable] = useState(false);
  // Post-deletion confirmation: the danger-zone flow signs the owner out
  // (all sessions revoked) and lands here. It leaves a one-shot flag so we
  // can confirm what happened + how to undo (founder request 2026-07-13),
  // instead of dropping them on a bare login screen.
  const [notice, setNotice] = useState<string | null>(null);
  // ADR-0201 follow-up — domain context carried from a public surface
  // (the hosted report page's claim CTA links
  // app.sill.so/sign-in?prefill_domain={domain}). Riding it on the
  // sign-in POST resumes the magic-link → welcome → onboarding chain
  // for that domain instead of dropping into an empty dashboard.
  const [prefillDomain, setPrefillDomain] = useState<string | null>(null);

  // The API's /v1/auth/verify redirects here with ?error=invalid_or_expired
  // when a magic link is stale, already-consumed, or otherwise rejected
  // (anti-enumeration collapses every failure cause to one code per
  // ADR-0011 §5.1). Read it on mount, surface in the existing error UI,
  // and strip the param from the URL so a refresh doesn't re-show it.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const err = url.searchParams.get('error');
    if (err === 'invalid_or_expired') {
      url.searchParams.delete('error');
      window.history.replaceState({}, '', url.toString());
      // If we know which email the user was trying to sign in with, auto-resend
      // so they don't have to type it again. The stored address is one-shot:
      // remove it immediately so a manual retry doesn't re-trigger the auto-send.
      try {
        const savedEmail = window.sessionStorage.getItem('sill_pending_sign_in_email') ?? '';
        if (savedEmail) {
          window.sessionStorage.removeItem('sill_pending_sign_in_email');
          setEmail(savedEmail);
          setPending(true);
          void api.auth.signIn(savedEmail).then(
            () => { setSent(true); setPending(false); },
            (e: unknown) => {
              if (e instanceof AuthRateLimitError) {
                setError(`Too many sign-in attempts. Try again in ${e.retry_after_minutes} min.`);
              } else {
                setError('Could not reach the sign-in service.');
                setErrorRetryable(true);
              }
              setPending(false);
            },
          );
          return;
        }
      } catch { /* sessionStorage unavailable — fall through */ }
      setError(
        'That sign-in link has expired or was already used. Enter your email below to send a new one.',
      );
    } else if (err === 'account_inactive') {
      // ADR-0148 follow-up — the link was valid but the account is
      // suspended/deactivated. Tell them clearly (no more silent refresh).
      setError(
        'Your account is no longer active. Contact your account owner to restore access.',
      );
      url.searchParams.delete('error');
      window.history.replaceState({}, '', url.toString());
    }
    // ADR-0201 follow-up — read ?prefill_domain via the SHARED
    // sanitizer (grammar parity with the server; a non-conforming value
    // silently drops, never blocks sign-in). Kept in the URL so a
    // refresh preserves the claim context.
    const prefill = sanitizePrefillDomainParam(
      url.searchParams.get('prefill_domain'),
    );
    if (prefill !== null) setPrefillDomain(prefill);
    // One-shot deletion confirmation left by the danger-zone flow.
    try {
      const flag = window.sessionStorage.getItem('sill_deletion_requested');
      if (flag !== null) {
        window.sessionStorage.removeItem('sill_deletion_requested');
        setNotice(
          'Your account is scheduled for deletion. Sign back in within 30 days to cancel it — after that, it is permanent.',
        );
      }
    } catch {
      // sessionStorage unavailable (private mode / SSR) — skip the notice.
    }
  }, []);

  const submit = async (): Promise<void> => {
    if (!email || pending) return;
    setPending(true);
    setError(null);
    setErrorRetryable(false);
    try {
      await api.auth.signIn(email, prefillDomain ?? undefined);
      setSent(true);
      try { window.sessionStorage.setItem('sill_pending_sign_in_email', email); } catch { /* private mode */ }
    } catch (err) {
      if (err instanceof AuthRateLimitError) {
        setError(
          `Too many sign-in attempts. Try again in ${err.retry_after_minutes} min.`,
        );
        setErrorRetryable(false);
      } else {
        // Network / 5xx / unexpected status — soft retry; do not flip
        // success state.
        setError('Could not reach the sign-in service.');
        setErrorRetryable(true);
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-8 py-20">
      {notice !== null ? (
        <div
          style={{
            background: C.surface,
            border: `1px solid ${C.amberFg ?? C.border}`,
            borderRadius: 4,
            padding: '14px 16px',
            marginBottom: 24,
          }}
        >
          <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.amberFg ?? C.muted, marginBottom: 6 }}>
            DELETION SCHEDULED
          </div>
          <p style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.6, margin: 0 }}>
            {notice}
          </p>
        </div>
      ) : null}
      <NumLabel n={0}>Sign in</NumLabel>
      <h1
        style={{
          fontFamily: serifFont,
          fontSize: 'clamp(44px, 8vw, 64px)',
          lineHeight: 1,
          color: C.text,
          letterSpacing: -2,
          marginBottom: 24,
        }}
      >
        Welcome back.
      </h1>

      {/* ADR-0201 follow-up — claim context from the report page. */}
      {prefillDomain !== null && !sent ? (
        <p
          style={{
            fontFamily: sansFont,
            fontSize: 14,
            color: C.body,
            lineHeight: 1.6,
            margin: '-8px 0 20px',
          }}
          data-testid="login-prefill-context"
        >
          Sign in and we&apos;ll pick up the setup for{' '}
          <span style={{ color: C.text, fontWeight: 600 }}>{prefillDomain}</span>.
        </p>
      ) : null}

      {sent ? (
        <div
          className="p-6"
          style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 4 }}
        >
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.limeFg,
              letterSpacing: 2,
              marginBottom: 8,
            }}
          >
            CHECK YOUR EMAIL
          </div>
          <p style={{ fontFamily: sansFont, fontSize: 15, color: C.text, lineHeight: 1.6 }}>
            We sent a sign-in link to <strong>{email}</strong>. Check your inbox and follow the link to sign in.
          </p>
          <button
            type="button"
            onClick={() => {
              setSent(false);
              setEmail('');
            }}
            className="mt-6"
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.muted,
              textDecoration: 'underline',
              background: 'transparent',
              border: 0,
              padding: 0,
            }}
          >
            Use a different address
          </button>
        </div>
      ) : (
        <>
          <p
            style={{
              fontFamily: sansFont,
              fontSize: 15,
              color: C.muted,
              lineHeight: 1.6,
              marginBottom: 24,
            }}
          >
            Magic link only. We send a one-time link to your inbox.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
            className="flex flex-col sm:flex-row items-stretch gap-2"
          >
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourdomain.com"
              className="flex-1 px-4 py-3 outline-none"
              style={{
                fontFamily: monoFont,
                fontSize: 14,
                color: C.text,
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
              }}
              aria-label="Email address"
            />
            <button
              type="submit"
              disabled={!email || pending}
              className="flex items-center justify-center gap-2 px-6 py-3"
              style={{
                background: email ? C.lime : C.surface2,
                color: email ? C.ink : C.muted,
                fontFamily: sansFont,
                fontWeight: 600,
                fontSize: 14,
                borderRadius: 2,
                opacity: pending ? 0.6 : 1,
              }}
            >
              {pending ? 'Sending…' : 'Send link'} <ArrowRight size={15} />
            </button>
          </form>
          {error ? (
            <div
              className="mt-3 p-3 flex items-center justify-between gap-3"
              style={{
                background: C.coralDim,
                border: `1px solid ${C.coralFg}`,
                borderRadius: 2,
                fontFamily: sansFont,
                fontSize: 13,
                color: C.text,
              }}
            >
              <span>{error}</span>
              {errorRetryable ? (
                <button
                  type="button"
                  onClick={() => { void submit(); }}
                  disabled={pending}
                  style={{
                    fontFamily: sansFont,
                    fontSize: 12,
                    fontWeight: 600,
                    color: C.text,
                    background: 'transparent',
                    border: `1px solid ${C.coralFg}`,
                    borderRadius: 2,
                    padding: '4px 10px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  Try again
                </button>
              ) : null}
            </div>
          ) : null}
          {/* Dev-only bypass: the real magic-link mailer ships with its own
              `security_review_required: yes` ADR. Gating on `import.meta.env.DEV`
              ensures a production build never exposes the no-op auth path. */}
          {import.meta.env.DEV ? (
            <div
              className="mt-12 pt-8"
              style={{ borderTop: `1px solid ${C.borderSoft}`, fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.6 }}
            >
              No account yet?{' '}
              <button
                type="button"
                onClick={() => setView('sites')}
                style={{
                  fontFamily: sansFont,
                  fontSize: 12,
                  color: C.limeFg,
                  textDecoration: 'underline',
                  background: 'transparent',
                  border: 0,
                  padding: 0,
                }}
              >
                Continue to dashboard
              </button>{' '}
              — dev-only bypass; production requires the magic link.
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
